import { CommandResult } from '@cl/shared/models';
import { FileMetadata } from '@cl/shared/models/file.model';
import { InvoicesService } from '@cl/core/services/invoices.service';
import { SinvoicesService } from '@cl/core/services/sinvoices.service';
import { ClientNotificationConfigModalComponent } from '@cl/shared/components/client-notification-config-modal/client-notification-config-modal.component';
import { Client } from '@cl/shared/models';
import { Invoice } from '@cl/shared/models/invoices/invoice.model';
import { Order } from '@cl/shared/models/orders/order.model';
import { Sinvoice } from '@cl/shared/models/sinvoices/sinvoice.model';
import { WebUser } from '@cl/shared/models/web-users/web-user.model';
import { environment } from '@env/environment';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { UsersService } from '../services';
import { InvoiceClientNotificationConfiguration } from '@cl/shared/models/invoices/invoice-client-notification-configuration.model';
import { EmployeeNotificationsService } from '../services/employee-notifications.service';

export abstract class BaseViewInvoiceComponent<T extends Invoice | Sinvoice, TService extends InvoicesService | SinvoicesService> {
    invoiceData: T;
    loading: boolean = false;

    constructor(
        protected service: TService,
        protected messageService: NzMessageService,
        protected modalService: NzModalService,
        protected usersService: UsersService,
        protected employeeNotificationsService: EmployeeNotificationsService,
    ) {}

    abstract edit(): void;
    abstract reloadInvoice(invoiceId: number): Promise<void>;

    gotoLuidaisInvoice(): void {
        if (this.invoiceData.isSinvoice) {
            window.open(`${environment.luidaisUrl}/ui/sbill.php?cmd=VIEW&id=${this.invoiceData.id}`, '_blank').focus();
            return;
        }

        window
            .open(
                `${environment.luidaisUrl}/ui/contract_bill.php?cmd=VIEW&cid=${this.invoiceData.contractId}&oid=${this.invoiceData.ordersId}&bid=${this.invoiceData.id}`,
                '_blank',
            )
            .focus();
    }

    async sendInvoiceNotification(): Promise<void> {
        try {
            this.loading = true;
            const client: Client = (await this.service.getInvoiceClient(this.invoiceData.id)).data;
            this.loading = false;
            if (!client) {
                this.messageService.error('Klients nav atrasts');
                return;
            }
            client.webUsers.map((webUser: WebUser): boolean => (webUser.selected = false));

            const modal: NzModalRef<ClientNotificationConfigModalComponent> = this.modalService.create({
                nzTitle: 'Nosūtīt atgādinājumu klientam',
                nzContent: ClientNotificationConfigModalComponent,
            });

            modal.afterOpen.subscribe(() => {
                modal.getContentComponent().loading = true;
                modal.getContentComponent().client = client;
                modal.getContentComponent().clientNotificationConfig = {
                    entityId: null,
                    webUserIds: null,
                    additionalMessage: null,
                };
                modal.getContentComponent().loading = false;
            });

            modal.afterClose.subscribe(async (invoiceNotificationConfig: InvoiceClientNotificationConfiguration): Promise<void> => {
                if (!invoiceNotificationConfig) {
                    this.messageService.warning('Atgādinājums nav nosūtīts');
                    return;
                }
                invoiceNotificationConfig.entityId = this.invoiceData.id;
                invoiceNotificationConfig.isSinvoice = this.invoiceData.isSinvoice;

                try {
                    this.loading = true;
                    await this.service.sendInvoiceNotification(invoiceNotificationConfig);
                    this.loading = false;
                    this.messageService.success('Atgādinājums veiksmīgi nosūtīts');
                    await this.reloadInvoice(this.invoiceData.id);
                } catch (ex) {
                    this.loading = false;
                    console.error(ex);
                    if (ex.error?.errors) {
                        ex.error.errors.forEach((error) => {
                            this.messageService.error(error);
                        });
                        return;
                    }

                    this.messageService.error('Kļūda nosūtot atgādinājumu');
                }
            });
        } catch (ex) {
            this.loading = false;
            console.error(ex);
            if (ex.error?.errors) {
                ex.error.errors.forEach((error) => {
                    this.messageService.error(error);
                });
                return;
            }

            this.messageService.error('Kļūda nosūtot atgādinājumu');
        }
    }

    async sendEmployeeInvoiceNotification(): Promise<void> {
        try {
            this.loading = true;
            if (this.invoiceData.isSinvoice) {
                await this.employeeNotificationsService.sendEmployeeNotification(this.invoiceData.id, this.service as SinvoicesService);
            } else {
                await this.employeeNotificationsService.sendEmployeeNotification(this.invoiceData.id, this.service as InvoicesService);
            }
        } finally {
            this.loading = false;
        }
    }

    readonly invoicePreviewMetadata = (): Promise<CommandResult<FileMetadata>> => this.service.getInvoiceFileMetadata(this.invoiceData.id);

    readonly invoicePreviewDownload = (): Promise<void> => this.service.downloadInvoice(this.invoiceData.id);

    async downloadInvoicePrintout(): Promise<void> {
        try {
            this.loading = true;
            await this.service.downloadInvoice(this.invoiceData.id);
            this.loading = false;
        } catch (ex) {
            this.loading = false;
            console.error(ex);
            if (ex.error?.errors) {
                ex.error.errors.forEach((error) => {
                    this.messageService.error(error);
                });
                return;
            }

            this.messageService.error(`Neizdevās lejupielādēt rēķinu`);
            return;
        }
    }

    async downloadInvoiceEInvoiceXML(): Promise<void> {
        try {
            this.loading = true;
            await this.service.downloadInvoiceEInvoiceXML(this.invoiceData.id);
        } catch (ex) {
            console.error(ex);
            if (ex.error?.errors) {
                ex.error.errors.forEach((error) => {
                    this.messageService.error(error);
                });
                return;
            }

            this.messageService.error(`Neizdevās lejupielādēt e-rēķinu`);
            return;
        } finally {
            this.loading = false;
        }
    }

    invoiceOrdersToString(orders: Order[]): string {
        return orders.map((o: Order): string => o.number).join(', ');
    }
}
